import { saveAs } from 'file-saver'
import I18n from 'i18n-js'
import callApi from 'services'
import eventService from 'services/eventService'
import { errorToast, successToast, warningToast } from 'utils/toast'
export const DEFAULT_SIZE = 10

const EXCLUDED_FILTER_KEYS = ['checkedPatrimonies']

function filtersToUrlParams(filters) {
  let url = ''
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && !EXCLUDED_FILTER_KEYS.includes(key)) {
        if (Array.isArray(value)) {
          url += `${key}=${value.join(',')}&`
        } else {
          url += `${key}=${value}&`
        }
      }
    })
  }
  return url
}

/**
 * Ajout de fichiers à un événement
 * @param {number} idEvent
 * @param {File[]} files - tous les fichiers à uploader
 * @param {number|null} statusId - id du statut (optionnel)
 * @param {File[]} filesToAnonymize - fichiers à flouter (tableau de fichiers)
 */
const uploadFiles = async (idEvent, files, statusId = null, filesToAnonymize = []) => {
  const newFiles = files.filter((file) => !file.id)

  if (newFiles.length === 0) {
    return Promise.resolve()
  }

  const filesToBlur = newFiles.filter((file) => filesToAnonymize.includes(file))
  const filesNoBlur = newFiles.filter((file) => !filesToAnonymize.includes(file))

  const uploadBatch = (fileBatch, anonymize) => {
    if (fileBatch.length === 0) {
      return Promise.resolve()
    }

    const formData = new FormData()
    fileBatch.forEach((file) => {
      formData.append('files', file)
    })

    if (statusId) {
      formData.append('statusId', statusId)
    }

    if (anonymize) {
      formData.append('anonymizeFaces', true)
    }

    return callApi(`events/${idEvent}/files`, 'POST', formData, undefined, true)
  }

  await uploadBatch(filesNoBlur, false)

  if (filesToBlur.length > 0) {
    try {
      await uploadBatch(filesToBlur, true)
      successToast(I18n.t('AnonymizeSuccess'))
    } catch {
      warningToast(I18n.t('AnonymizeError'))
      await uploadBatch(filesToBlur, false)
    }
  }
}

/**
 * Suppression d'un fichier
 * @param {number} idFile
 */
const deleteFile = (idFile) => {
  return callApi(`events/files/${idFile}`, 'DELETE')
}

const base64ToBlob = (base64, type) => {
  const byteCharacters = atob(base64)
  const byteNumbers = new Array(byteCharacters.length)
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }
  const byteArray = new Uint8Array(byteNumbers)
  return new Blob([byteArray], { type })
}

/**
 * Téléchargement d'un fichier
 * @param {number} idFile
 * @param {boolean} open si true, ouvre le fichier dans un nouvel onglet et ne le télécharge pas
 */
const downloadFile = (idFile) => {
  return callApi(`events/files/${idFile}/download`, 'GET').then(async ({ filename, content }) => {
    saveAs(base64ToBlob(content, 'application/octet-stream'), filename)
  })
}

export default {
  getEventList(filters, page, size = DEFAULT_SIZE, isMap = false) {
    let url = `events?`
    if (page) {
      url += `&offset=${page * size}`
    }
    if (size) {
      url += `&size=${size}`
    }
    if (isMap) {
      url += `&isMap=true`
    }

    url += `&${filtersToUrlParams(filters)}`
    return callApi(url, 'GET')
  },
  getEventTypes() {
    return callApi('types', 'GET')
  },
  getLocationTypes() {
    return callApi('locationTypes', 'GET')
  },
  createEvent(event) {
    const eventApi = eventService.generateEventForCreation(event)
    return callApi('events', 'POST', JSON.stringify(eventApi))
  },
  updateEvent(event) {
    const eventApi = eventService.generateEventForCreation(event)
    return callApi(`events/${event.id}`, 'PUT', JSON.stringify(eventApi))
  },
  getEventAggregation(filters, criteria) {
    let groupBy = ''
    if (Array.isArray(criteria)) {
      criteria.forEach((c) => (groupBy += `groupBy=${c}&`))
    } else {
      groupBy = `groupBy=${criteria}`
    }
    return callApi(`events/aggregation?${groupBy}&${filtersToUrlParams(filters)}`, 'GET')
  },
  getEventStatistics(filters) {
    return callApi(`events/statistics?${filtersToUrlParams(filters)}`, 'GET')
  },
  getEvent(id) {
    return callApi(`events/${id}`, 'GET')
  },
  deleteEvent(id) {
    return callApi(`events/${id}`, 'DELETE')
  },
  createStatus(eventId, status) {
    return callApi(`events/${eventId}/status`, 'POST', JSON.stringify(status))
  },
  updateEventsStatus(status) {
    return callApi(`events/status`, 'POST', JSON.stringify(status))
  },
  updateStatus(eventId, status) {
    return callApi(`events/${eventId}/status/${status.id}`, 'PUT', JSON.stringify(status))
  },
  exportEvents(type, filters) {
    return callApi(`events/export?exportType=${type}&${filtersToUrlParams(filters)}`, 'GET').catch(() => {
      errorToast(I18n.t('ExportError'))
    })
  },
  uploadFiles,
  deleteFile,
  downloadFile,
}
